import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFieldArray, useForm } from 'react-hook-form';
import { api } from '../api/client';
import { useState } from 'react';
import TableToolbar from './TableToolbar';
import SubTabs from './SubTabs';

type PurchaseOrder = {
  id: number;
  supplier_id: number;
  supplier_name: string;
  status: string;
  expected_date?: string;
  reference?: string;
  notes?: string;
  item_count: number;
};

type PurchaseOrderForm = {
  supplier_id: number;
  expected_date?: string;
  reference?: string;
  notes?: string;
  items: Array<{
    product_id: number;
    quantity: number;
    unit_cost: number;
  }>;
};

const PurchaseOrdersPage = () => {
  const queryClient = useQueryClient();
  const ordersQuery = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: () => api.get<PurchaseOrder[]>('/api/purchase-orders')
  });

  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<Array<{ id: number; name: string }>>('/api/products')
  });

  const suppliersQuery = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<Array<{ id: number; name: string }>>('/api/suppliers')
  });

  const form = useForm<PurchaseOrderForm>({
    defaultValues: {
      items: [{ product_id: 0, quantity: 1, unit_cost: 0 }]
    }
  });
  const items = useFieldArray({ name: 'items', control: form.control });

  const createOrder = useMutation({
    mutationFn: (values: PurchaseOrderForm) =>
      api.post('/api/purchase-orders', {
        ...values,
        supplier_id: Number(values.supplier_id),
        items: values.items
          .filter((item) => item.product_id)
          .map((item) => ({
            product_id: Number(item.product_id),
            quantity: Number(item.quantity),
            unit_cost: Number(item.unit_cost)
          }))
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      form.reset({
        supplier_id: 0,
        items: [{ product_id: 0, quantity: 1, unit_cost: 0 }]
      });
    }
  });

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<keyof PurchaseOrder>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'orders' | 'create'>('orders');
  const orders = useMemo(() => {
    const rows = (ordersQuery.data ?? []).filter((o) => {
      if (!filter) return true;
      const f = filter.toLowerCase();
      const hay = [String(o.id), o.supplier_name, o.status, o.reference ?? '', String(o.item_count), o.expected_date ?? '']
        .join(' ')
        .toLowerCase();
      return hay.includes(f);
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const va = a[sortKey] as unknown;
      const vb = b[sortKey] as unknown;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va ?? '').localeCompare(String(vb ?? '')) * dir;
    });
  }, [ordersQuery.data, filter, sortKey, sortDir]);

  return (
    <div>
      <h2>Purchase Orders</h2>
      <p style={{ color: '#64748b' }}>
        Generate purchase orders for green coffee and consumables. Receipts automatically
        create inventory movements via Workers.
      </p>

      <SubTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'orders', label: 'Recent Orders', badge: orders.length },
          { id: 'create', label: 'Create Purchase Order' }
        ]}
      />

      {activeTab === 'orders' ? (
        <section style={{ marginTop: 8 }}>
          <h3>Recent orders</h3>
          <TableToolbar value={filter} onChange={setFilter} placeholder="Filter purchase orders…" />
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  {([
                    { key: 'id', label: 'ID' },
                    { key: 'supplier_name', label: 'Supplier' } as any,
                    { key: 'status', label: 'Status' },
                    { key: 'expected_date', label: 'Expected' } as any,
                    { key: 'reference', label: 'Reference' } as any,
                    { key: 'item_count', label: 'Items' } as any
                  ] as Array<{ key: keyof PurchaseOrder | any; label: string }>).map((col) => (
                    <th
                      key={String(col.key)}
                      onClick={() => {
                        if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setSortKey(col.key as keyof PurchaseOrder);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {col.label}
                      {sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.id}</td>
                    <td>{order.supplier_name}</td>
                    <td>
                      <span className={`status-chip ${order.status}`}>{order.status}</span>
                    </td>
                    <td>{order.expected_date ? new Date(order.expected_date).toLocaleDateString() : '—'}</td>
                    <td>{order.reference ?? '—'}</td>
                    <td>{order.item_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab === 'create' ? (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Create purchase order</h3>
          <form className="form-grid" onSubmit={form.handleSubmit((values) => createOrder.mutate(values))}>
            <div className="form-group">
              <label>Supplier</label>
              <select {...form.register('supplier_id', { required: true, valueAsNumber: true })}>
                <option value="">Select supplier</option>
                {suppliersQuery.data?.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Expected date</label>
              <input type="date" {...form.register('expected_date')} />
            </div>
            <div className="form-group">
              <label>Reference</label>
              <input {...form.register('reference')} />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Notes</label>
              <textarea rows={2} {...form.register('notes')} />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <h4>Line items</h4>
              {items.fields.map((field, index) => (
                <div
                  key={field.id}
                  style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, marginBottom: 8 }}
                >
                  <select {...form.register(`items.${index}.product_id` as const, { valueAsNumber: true })}>
                    <option value="">Select product</option>
                    {productsQuery.data?.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.1"
                    {...form.register(`items.${index}.quantity` as const, { valueAsNumber: true })}
                  />
                  <input
                    type="number"
                    step="0.01"
                    {...form.register(`items.${index}.unit_cost` as const, { valueAsNumber: true })}
                  />
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={() => items.remove(index)}
                    disabled={items.fields.length === 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn secondary"
                onClick={() => items.append({ product_id: 0, quantity: 1, unit_cost: 0 })}
                style={{ marginTop: 8 }}
              >
                Add line
              </button>
            </div>

            <button className="btn primary" type="submit" disabled={createOrder.isPending}>
              {createOrder.isPending ? 'Creating…' : 'Submit purchase order'}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default PurchaseOrdersPage;
